/**
 * SIL-1351 - the countered-offer row: a warn badge reading "Countered at $X"
 * plus Accept and Decline. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 4.6.
 *
 * ## Which endpoint Decline calls, and why it is not `/reject`
 *
 * The spec and the Linear issue both say Decline calls `/reject`. It cannot:
 * `POST /api/client/offers/:id/reject` is a SELLER route - `access({
 * capability: 'sell' })` plus an explicit `canSell` check, delegating to
 * `sellerClose(..., 'rejected')` (apps/api/src/routes/client/offers.ts). A
 * buyer calling it gets a 403 every time, so shipping it would mean shipping a
 * button that is guaranteed to fail.
 *
 * The buyer's decline-a-counter endpoint already exists and is
 * `POST /api/client/offers/:id/withdraw`. That is not a substitution: the
 * offer DTO spells the model out - "the buyer has exactly two moves: accept
 * the counter (-> 'accepted' at the countered price) or decline (-> 'withdrawn'
 * - no separate terminal for declining)" (packages/shared/src/dto/offer.ts).
 * `/reject` is the seller's name for the same idea, which is where the spec's
 * wording comes from. No new endpoint is added either way, which is the
 * constraint the issue actually cares about.
 *
 * Both mutations run through `useVerificationGate().guard`, so the SERVER
 * decides: the action is attempted and a 403 opens the verification modal,
 * rather than the client pre-checking a status it may hold stale.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Offer } from '@siliconx/shared/dto/offer';
import { api, throwIfNotOk } from '../../../lib/api';
import { formatUsd } from '../../../lib/format';
import { qk } from '../../../lib/queryKeys';
import { exchangeErrorMessage } from '../errors';
import { useVerificationGate } from '../VerificationGateProvider';
import {
  COUNTER_ACCEPT_ERROR_FALLBACK,
  COUNTER_ACCEPT_LABEL,
  COUNTER_ACCEPT_PENDING_LABEL,
  COUNTER_ACCEPTED_TOAST,
  COUNTER_DECLINE_ERROR_FALLBACK,
  COUNTER_DECLINE_LABEL,
  COUNTER_DECLINE_PENDING_LABEL,
  COUNTER_DECLINED_TOAST,
  counteredAtLabel,
} from '../copy/activity';

/**
 * The warn-variant badge for a countered offer. Amber is the app's warn style
 * (see `OfferStatusBadge`'s `pending`); the generic blue `countered` badge is
 * deliberately not reused here, because this badge carries the counter AMOUNT
 * and the design calls it out as the one row on the page needing a decision.
 */
export function CounteredBadge({ counterAmountUsd }: { counterAmountUsd: number }) {
  return (
    <span className="inline-flex w-fit rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      {counteredAtLabel(formatUsd(counterAmountUsd))}
    </span>
  );
}

const ACTION_CLASS =
  'rounded border px-2 py-1 text-xs font-medium disabled:opacity-50';

export function CounteredOfferActions({ offer }: { offer: Offer }) {
  const queryClient = useQueryClient();
  const { guard } = useVerificationGate();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.offers() });

  const acceptCounter = useMutation({
    mutationFn: async () =>
      guard(async () => {
        const res = await api.api.client.offers[':id']['accept-counter'].$post({
          param: { id: offer.id },
        });
        await throwIfNotOk(res);
      }),
    onSuccess: () => {
      toast.success(COUNTER_ACCEPTED_TOAST);
      void invalidate();
    },
    onError: (err: unknown) =>
      toast.error(exchangeErrorMessage(err, COUNTER_ACCEPT_ERROR_FALLBACK)),
  });

  // See the module header: `/withdraw` IS the buyer's decline-a-counter route.
  const declineCounter = useMutation({
    mutationFn: async () =>
      guard(async () => {
        const res = await api.api.client.offers[':id'].withdraw.$post({
          param: { id: offer.id },
        });
        await throwIfNotOk(res);
      }),
    onSuccess: () => {
      toast.success(COUNTER_DECLINED_TOAST);
      void invalidate();
    },
    onError: (err: unknown) =>
      toast.error(exchangeErrorMessage(err, COUNTER_DECLINE_ERROR_FALLBACK)),
  });

  const busy = acceptCounter.isPending || declineCounter.isPending;

  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => acceptCounter.mutate()}
        disabled={busy}
        className={`${ACTION_CLASS} border-green-300 text-green-700 hover:bg-green-50`}
      >
        {acceptCounter.isPending ? COUNTER_ACCEPT_PENDING_LABEL : COUNTER_ACCEPT_LABEL}
      </button>
      <button
        type="button"
        onClick={() => declineCounter.mutate()}
        disabled={busy}
        className={`${ACTION_CLASS} border-border text-muted-foreground hover:bg-muted`}
      >
        {declineCounter.isPending ? COUNTER_DECLINE_PENDING_LABEL : COUNTER_DECLINE_LABEL}
      </button>
    </div>
  );
}
